import type {
  GuestSavedSearch as SavedSearch,
  GuestSavedSpot as SavedSpot,
  GuestStorageSnapshot
} from "../services/guest-storage";
import { getSupabaseClient } from "../supabase/client";

export type AccountLibrarySnapshot = Pick<GuestStorageSnapshot, "savedSpots" | "savedSearches">;

export type GuestImportResult = {
  mergedSpotCount: number;
  mergedSearchCount: number;
  skippedSpotCount: number;
  importedSpotIds: string[];
  importedSearchIds: string[];
};

type SavedSpotRow = {
  spot_id: string;
  created_at: string;
};

type SpotSummaryRow = {
  id: string;
  name: string;
  waterbody: string;
};

type SavedSearchRow = {
  id: string;
  name: string;
  filters: Record<string, string | number | boolean>;
  created_at: string;
};

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export async function loadAccountLibrary(userId: string): Promise<AccountLibrarySnapshot> {
  const client = requireClient();
  const [savedSpotsResponse, savedSearchesResponse] = await Promise.all([
    client
      .from("saved_spots")
      .select("spot_id,created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false }),
    client
      .from("saved_searches")
      .select("id,name,filters,created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
  ]);

  if (savedSpotsResponse.error || savedSearchesResponse.error) {
    throw new Error("Your synced saves could not be refreshed.");
  }

  const savedSpotRows = (savedSpotsResponse.data ?? []) as SavedSpotRow[];
  const spotIds = savedSpotRows.map((row) => row.spot_id);
  const summaries = new Map<string, SpotSummaryRow>();

  if (spotIds.length > 0) {
    const { data, error } = await client
      .from("spots")
      .select("id,name,waterbody")
      .in("id", spotIds);

    if (error) {
      throw new Error("Saved Spot details could not be refreshed.");
    }

    for (const spot of (data ?? []) as SpotSummaryRow[]) {
      summaries.set(spot.id, spot);
    }
  }

  return {
    savedSpots: savedSpotRows.map((row) => {
      const spot = summaries.get(row.spot_id);
      return {
        spotId: row.spot_id,
        name: spot?.name ?? "Unavailable spot",
        waterbody: spot?.waterbody ?? "No longer available",
        location: spot ? "North America" : "Account save",
        savedAt: row.created_at
      };
    }),
    savedSearches: ((savedSearchesResponse.data ?? []) as SavedSearchRow[]).map(normalizeSearchRow)
  };
}

export async function saveAccountSpot(userId: string, spot: Omit<SavedSpot, "savedAt">) {
  const client = requireClient();
  const { error } = await client
    .from("saved_spots")
    .upsert(
      { user_id: userId, spot_id: spot.spotId },
      { onConflict: "user_id,spot_id", ignoreDuplicates: true }
    );

  if (error) {
    throw new Error("This Spot could not be saved to your account.");
  }

  return { ...spot, savedAt: new Date().toISOString() } satisfies SavedSpot;
}

export async function removeAccountSpot(userId: string, spotId: string) {
  const client = requireClient();
  const { error } = await client
    .from("saved_spots")
    .delete()
    .eq("user_id", userId)
    .eq("spot_id", spotId);

  if (error) {
    throw new Error("This Spot could not be removed from your account.");
  }
}

export async function saveAccountSearch(
  userId: string,
  search: Omit<SavedSearch, "id" | "createdAt">
) {
  const client = requireClient();
  const { data: existingRows, error: existingError } = await client
    .from("saved_searches")
    .select("id,name,filters,created_at")
    .eq("user_id", userId);

  if (existingError) {
    throw new Error("Your saved searches could not be checked.");
  }

  const existing = ((existingRows ?? []) as SavedSearchRow[]).find(
    (row) => serializeFilters(row.filters) === serializeFilters(search.filters)
  );

  if (existing) {
    const { data, error } = await client
      .from("saved_searches")
      .update({ name: search.label })
      .eq("user_id", userId)
      .eq("id", existing.id)
      .select("id,name,filters,created_at")
      .single();

    if (error || !data) {
      throw new Error("This search could not be updated in your account.");
    }

    return normalizeSearchRow(data as SavedSearchRow);
  }

  const { data, error } = await client
    .from("saved_searches")
    .insert({ user_id: userId, name: search.label, filters: search.filters })
    .select("id,name,filters,created_at")
    .single();

  if (error || !data) {
    throw new Error("This search could not be saved to your account.");
  }

  return normalizeSearchRow(data as SavedSearchRow);
}

export async function removeAccountSearch(userId: string, searchId: string) {
  const client = requireClient();
  const { error } = await client
    .from("saved_searches")
    .delete()
    .eq("user_id", userId)
    .eq("id", searchId);

  if (error) {
    throw new Error("This search could not be removed from your account.");
  }
}

export async function importGuestLibrary(
  userId: string,
  snapshot: GuestStorageSnapshot
): Promise<GuestImportResult> {
  const client = requireClient();
  const importableSpots = snapshot.savedSpots.filter((spot) => uuidPattern.test(spot.spotId));

  if (importableSpots.length > 0) {
    const { error } = await client
      .from("saved_spots")
      .upsert(
        importableSpots.map((spot) => ({ user_id: userId, spot_id: spot.spotId })),
        { onConflict: "user_id,spot_id", ignoreDuplicates: true }
      );

    if (error) {
      throw new Error("Your device Spot saves could not be imported.");
    }
  }

  const { data: existingRows, error: existingError } = await client
    .from("saved_searches")
    .select("id,filters")
    .eq("user_id", userId);

  if (existingError) {
    throw new Error("Your account searches could not be checked for import.");
  }

  const existingFilters = new Set(
    (existingRows ?? []).map((row) => serializeFilters(row.filters as Record<string, string | number | boolean>))
  );
  const searchesToInsert = snapshot.savedSearches.filter(
    (search) => !existingFilters.has(serializeFilters(search.filters))
  );

  if (searchesToInsert.length > 0) {
    const { error } = await client.from("saved_searches").insert(
      searchesToInsert.map((search) => ({
        user_id: userId,
        name: search.label,
        filters: search.filters
      }))
    );

    if (error) {
      throw new Error("Your device searches could not be imported.");
    }
  }

  return {
    mergedSpotCount: importableSpots.length,
    mergedSearchCount: snapshot.savedSearches.length,
    skippedSpotCount: snapshot.savedSpots.length - importableSpots.length,
    importedSpotIds: importableSpots.map((spot) => spot.spotId),
    importedSearchIds: snapshot.savedSearches.map((search) => search.id)
  };
}

export function isSyncableSpotId(spotId: string) {
  return uuidPattern.test(spotId);
}

function normalizeSearchRow(row: SavedSearchRow): SavedSearch {
  return {
    id: row.id,
    label: row.name,
    filters: row.filters,
    createdAt: row.created_at
  };
}

function serializeFilters(filters: Record<string, string | number | boolean>) {
  return JSON.stringify(
    Object.fromEntries(Object.entries(filters).sort(([first], [second]) => first.localeCompare(second)))
  );
}

function requireClient() {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error("Account synchronization is not configured.");
  }
  return client;
}
